import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Clock,
  MapPin,
  Monitor,
  CalendarCheck,
  Shield,
  Sparkles,
  Star,
  Quote,
  ChevronDown,
} from "lucide-react";
import { LANDINGS, LANDING_BONUS } from "../data/landings";
import { TESTIMONIALS, courseUrl } from "../mock";
import { getSchedule } from "../data/schedule";
import CourseScheduleCard from "./CourseScheduleCard";
import DownloadProgramBox from "./DownloadProgramBox";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Reveal from "./ui/Reveal";
import Seo from "./Seo";
import { trackLandingView, trackLandingCtaClick, trackCourseLead, withTrackingParams } from "../lib/analytics";
import { api } from "../lib/api";

/** A/B test: assegnazione sticky per browser (A = copy originale, B = variante). */
function getAbVariant() {
  try {
    let v = localStorage.getItem("mda_ab_variant");
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem("mda_ab_variant", v);
    }
    return v;
  } catch (e) {
    return "A";
  }
}

const Stars = () => (
  <div className="flex items-center gap-0.5" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={14} className="fill-[#FBBC05] text-[#FBBC05]" />
    ))}
  </div>
);

/** Microcopy sui formati disponibili — usata vicino alle CTA principali. */
const FormatInfo = ({ align = "center", className = "" }) => (
  <div
    className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-white/55 text-xs sm:text-sm ${
      align === "center" ? "justify-center" : "justify-start"
    } ${className}`}
    data-testid="landing-format-info"
  >
    <span className="inline-flex items-center gap-1.5">
      <MapPin size={13} style={{ color: "#d4ff3d" }} /> In aula, in presenza
    </span>
    <span className="inline-flex items-center gap-1.5">
      <Monitor size={13} style={{ color: "#d4ff3d" }} /> Online one&#8209;to&#8209;one · sessioni da 4h
    </span>
    <span className="inline-flex items-center gap-1.5">
      <CalendarCheck size={13} style={{ color: "#d4ff3d" }} /> Data personalizzabile
    </span>
  </div>
);

/**
 * LandingPage — reusable template for each course-specific landing.
 * Route: /landing-corso-:slug (slug ∈ cqo|smm|fba|cl|ai)
 */
export default function LandingPage({ slugParam, onOpenBooking }) {
  const navigate = useNavigate();
  const data = LANDINGS[slugParam];
  const abVariant = getAbVariant();
  const copy = data && abVariant === "B" && data.variantB ? { ...data, ...data.variantB } : data;

  useEffect(() => {
    if (!data) return;
    trackLandingView({ landingSlug: data.slug, courseSlug: data.courseSlug, abVariant });
    const key = `mda_ab_view_${data.slug}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      api.post("/track/ab", { landing_slug: data.slug, variant: abVariant, event: "view" }).catch(() => {});
    }
  }, [data, abVariant]);

  if (!data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl mb-4">Landing non trovata</h1>
          <Link
            to="/"
            className="underline text-white/60 hover:text-white"
            data-testid="landing-notfound-home-link"
          >
            Torna alla home
          </Link>
        </div>
      </div>
    );
  }

  const goToCourse = (location) => {
    trackLandingCtaClick({
      landingSlug: data.slug,
      courseSlug: data.courseSlug,
      ctaLocation: location,
      abVariant,
    });
    api.post("/track/ab", { landing_slug: data.slug, variant: abVariant, event: "cta_click" }).catch(() => {});
    // Meta Pixel Lead (fires before navigation so it is never lost)
    trackCourseLead({ courseName: data.courseName });
    navigate(withTrackingParams(courseUrl(data.courseSlug)));
  };

  const schedule = getSchedule(data.courseSlug);

  return (
    <div className="App grain bg-[#0a0a0a] text-white">
      <Seo
        title={`${data.courseName} — MyDigitalAction`}
        description={data.hookSubtitle || undefined}
        noindex
        canonical={`https://mydigitalaction.it${courseUrl(data.courseSlug)}`}
      />
      <Navbar onOpenBooking={onOpenBooking} />

      {/* ========================= HERO ========================= */}
      <section
        className="relative overflow-hidden pt-24 lg:pt-32 pb-16 lg:pb-24"
        data-testid={`landing-${data.slug}-hero`}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.65]"
          style={{ backgroundImage: `url(${data.heroImage})` }}
          aria-hidden
        />
        {/* Vertical fade into the page background */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-[#0a0a0a]"
          aria-hidden
        />
        {/* Horizontal shade to keep the left-side copy legible while the building stays visible on the right */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-transparent"
          aria-hidden
        />

        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT — copy */}
            <Reveal variant="fade-up" className="lg:col-span-7 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-lime/40 bg-lime/5 text-[11px] font-mono uppercase tracking-wider" style={{ color: "#d4ff3d", borderColor: "rgba(212,255,61,0.3)" }}>
                  <Sparkles size={12} /> {data.heroLabel}
                </span>
                <span className="text-white/40 text-xs font-mono uppercase tracking-widest hidden sm:inline">
                  {data.hours}
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-[clamp(2.25rem,3.6vw,3.3rem)] leading-[1.05] tracking-tight">
                {copy.hookHeadline}
                <br />
                <span style={{ color: "#d4ff3d" }} className="italic font-light">
                  {copy.hookHeadlineAccent}
                </span>
              </h1>

              <p className="mt-6 text-white/75 text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {data.hookSubtitle}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start items-center">
                <button
                  type="button"
                  onClick={() => goToCourse("hero")}
                  className="btn-primary group inline-flex items-center gap-2 justify-center"
                  data-testid={`landing-${data.slug}-cta-hero`}
                >
                  <span className="flex flex-col items-center leading-none">
                    <span>{copy.ctaPrimary}</span>
                    <span className="text-[11px] font-normal opacity-70 mt-1">Offerta lancio — €{data.price}</span>
                  </span>
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </button>
                <div className="flex items-center gap-2 text-white/50 text-sm sm:pl-3">
                  <span className="line-through text-white/30">€{data.originalPrice}</span>
                  <span style={{ color: "#d4ff3d" }} className="font-mono text-xs">
                    {data.ctaSecondary}
                  </span>
                </div>
              </div>

              <div className="mt-8">
                <FormatInfo align="center" className="lg:justify-start" />
              </div>

              <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-3 text-white/60 text-sm">
                <span className="inline-flex items-center gap-2">
                  <MapPin size={14} /> Pisa — Montacchiello Campus
                </span>
                <span className="inline-flex items-center gap-2">
                  <Shield size={14} /> Garanzia soddisfatti o rimborsati
                </span>
              </div>
            </Reveal>

            {/* RIGHT — scheda date prominente */}
            {schedule && (
              <Reveal variant="fade-up" className="lg:col-span-5">
                <CourseScheduleCard
                  vertical
                  schedule={schedule}
                  testId={`landing-${data.slug}-schedule-card`}
                  ctaLabel="Scopri il corso"
                  onCta={() => goToCourse("hero-schedule")}
                />
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* ========================= FORMATI ========================= */}
      <section className="py-16 lg:py-20 bg-[#0c0c0c] border-y border-white/5" data-testid={`landing-${data.slug}-formati`}>
        <div className="container-max max-w-5xl">
          <span className="section-label">Come si svolge</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-2xl lg:text-4xl mt-4 mb-3 leading-tight"
          >
            Scegli il formato più adatto a te
          </Reveal>
          <p className="text-white/60 text-base lg:text-lg leading-relaxed mb-10 max-w-2xl">
            Lo stesso metodo, due modalità. In entrambi i casi puoi{" "}
            <strong className="text-white/90">personalizzare la data</strong> in base alle tue esigenze.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Reveal variant="fade-up" className="p-6 lg:p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-3">
                <MapPin size={22} style={{ color: "#d4ff3d" }} />
                <h3 className="font-display text-white text-xl">In presenza, in aula</h3>
              </div>
              <p className="text-white/70 text-sm lg:text-base leading-relaxed">
                Vivi il corso dal vivo nel nostro campus, con affiancamento diretto e
                confronto con gli altri partecipanti. Massimo 12 persone per garantire
                attenzione reale a ciascuno.
              </p>
            </Reveal>
            <Reveal variant="fade-up" delay={100} className="p-6 lg:p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-3">
                <Monitor size={22} style={{ color: "#d4ff3d" }} />
                <h3 className="font-display text-white text-xl">Online, one&#8209;to&#8209;one</h3>
              </div>
              <p className="text-white/70 text-sm lg:text-base leading-relaxed">
                Sessioni individuali da <strong className="text-white/90">4 ore</strong>,
                interamente dedicate a te e alla tua attività. Massima flessibilità sugli
                orari e sui contenuti, comodamente da remoto.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ========================= PAIN POINTS ========================= */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="container-max max-w-4xl">
          <span className="section-label">Riconosci il problema</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-12 leading-tight"
          >
            {data.painsTitle}
          </Reveal>
          <div className="space-y-4">
            {data.pains.map((p, i) => (
              <Reveal
                key={i}
                variant="fade-up"
                delay={i * 80}
                className="flex gap-4 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <span
                  className="font-display text-2xl leading-none flex-shrink-0"
                  style={{ color: "#d4ff3d" }}
                >
                  0{i + 1}
                </span>
                <p className="text-white/80 text-base lg:text-lg leading-relaxed">
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= SOLUTION ========================= */}
      <section className="py-20 lg:py-28 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max max-w-4xl">
          <span className="section-label">La soluzione</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-8 leading-tight"
          >
            {data.solutionTitle}
          </Reveal>
          <Reveal
            variant="fade-up"
            delay={100}
            className="text-white/75 text-lg lg:text-xl leading-relaxed"
          >
            {data.solutionText}
          </Reveal>
        </div>
      </section>

      {/* ========================= LEARN ========================= */}
      <section className="py-20 lg:py-28">
        <div className="container-max max-w-5xl">
          <span className="section-label">Il programma</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-12 leading-tight"
          >
            {data.learnTitle}
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.learnItems.map((item, i) => (
              <Reveal
                key={i}
                variant="fade-up"
                delay={i * 60}
                className="flex gap-3 p-5 rounded-xl border border-white/10 bg-white/[0.02]"
              >
                <Check
                  size={20}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: "#d4ff3d" }}
                />
                <p className="text-white/85 text-base leading-relaxed">
                  {item}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {data.courseSlug === "social-media-marketing" && (
        <section className="pb-4" data-testid="landing-program-download-section">
          <div className="container-max max-w-5xl">
            <DownloadProgramBox slug="social-media-marketing" programName="Social Media Marketing" />
          </div>
        </section>
      )}

      {/* ========================= MID CTA ========================= */}
      <section className="py-16 border-y border-lime/20" style={{ borderColor: "rgba(212,255,61,0.15)" }}>
        <div className="container-max text-center">
          <Reveal variant="zoom" as="h3" className="font-display text-2xl lg:text-4xl mb-4">
            Pronto a iniziare? Le prossime date hanno posti limitati.
          </Reveal>
          <p className="text-white/60 mb-8">Max 12 partecipanti per garantire l&apos;affiancamento vero.</p>
          <button
            type="button"
            onClick={() => goToCourse("mid")}
            className="btn-primary inline-flex items-center gap-2"
            data-testid={`landing-${data.slug}-cta-mid`}
          >
            <span className="flex flex-col items-center leading-none">
              <span>{data.ctaPrimary}</span>
              <span className="text-[11px] font-normal opacity-70 mt-1">Offerta lancio — €{data.price}</span>
            </span>
            <ArrowRight size={16} />
          </button>
          <div className="mt-6">
            <FormatInfo align="center" />
          </div>
        </div>
      </section>

      {/* ========================= TARGET ========================= */}
      <section className="py-20 lg:py-28 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max max-w-4xl">
          <span className="section-label">Per te se</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-12 leading-tight"
          >
            {data.targetTitle}
          </Reveal>
          <div className="space-y-3">
            {data.targetItems.map((item, i) => (
              <Reveal
                key={i}
                variant="fade-up"
                delay={i * 80}
                className="flex gap-4 items-start"
              >
                <div
                  className="w-8 h-8 rounded-full border flex-shrink-0 flex items-center justify-center mt-0.5"
                  style={{ borderColor: "rgba(212,255,61,0.4)" }}
                >
                  <Check size={14} style={{ color: "#d4ff3d" }} />
                </div>
                <p className="text-white/85 text-lg leading-relaxed pt-1">
                  {item}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= BONUS ========================= */}
      <section className="py-20 lg:py-28">
        <div className="container-max max-w-5xl">
          <span className="section-label">Incluso nel prezzo</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-12 leading-tight"
          >
            Bonus per chi si iscrive ora
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {LANDING_BONUS.map((b, i) => (
              <Reveal
                key={i}
                variant="fade-up"
                delay={i * 80}
                className="p-6 lg:p-8 rounded-2xl border border-white/10 bg-white/[0.02]"
              >
                <div className="flex items-start gap-4">
                  <Sparkles
                    size={22}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: "#d4ff3d" }}
                  />
                  <div>
                    <h3 className="font-display text-white text-xl mb-2">
                      {b.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= GUARANTEE ========================= */}
      <section className="py-16 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max max-w-3xl text-center">
          <Shield
            size={40}
            className="mx-auto mb-6"
            style={{ color: "#d4ff3d" }}
          />
          <Reveal
            variant="zoom"
            as="h2"
            className="font-display text-2xl lg:text-4xl mb-4"
          >
            Garanzia zero rischi
          </Reveal>
          <p className="text-white/70 text-base lg:text-lg leading-relaxed">
            Se entro le 13:00 del primo giorno di corso capisci che non fa per te,
            ti rimborsiamo l&apos;intera quota. E il workbook resta tuo, come nostro
            omaggio.
          </p>
        </div>
      </section>

      {/* ========================= TESTIMONIALS ========================= */}
      <section className="py-20 lg:py-28">
        <div className="container-max">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <span className="section-label">Cosa dicono di noi</span>
              <Reveal
                variant="fade-up"
                as="h2"
                className="font-display text-3xl lg:text-5xl mt-5 leading-tight"
              >
                Recensioni verificate Google
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal
                key={i}
                variant="fade-up"
                delay={i * 80}
                className="p-6 lg:p-7 rounded-2xl border border-white/10 bg-white/[0.02]"
                data-testid={`landing-testimonial-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <Quote size={22} style={{ color: "#d4ff3d" }} />
                  <Stars />
                </div>
                <p className="text-white/85 text-base leading-relaxed">
                  {t.text}
                </p>
                <div className="mt-6 pt-4 border-t border-white/10">
                  <div className="font-display text-white text-sm">
                    {t.name}
                  </div>
                  <div className="text-white/50 text-xs mt-0.5">{t.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= FAQ ========================= */}
      <section className="py-20 lg:py-28 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max max-w-3xl">
          <span className="section-label">Le domande di chi ha già fatto il corso</span>
          <Reveal
            variant="fade-up"
            as="h2"
            className="font-display text-3xl lg:text-5xl mt-5 mb-10 leading-tight"
          >
            Domande frequenti
          </Reveal>
          <div className="space-y-3">
            {data.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
                data-testid={`landing-faq-${i}`}
              >
                <summary className="cursor-pointer flex items-center justify-between p-5 lg:p-6 select-none">
                  <span className="font-display text-white text-base lg:text-lg pr-4">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className="text-white/60 group-open:rotate-180 transition-transform flex-shrink-0"
                  />
                </summary>
                <div className="px-5 lg:px-6 pb-5 lg:pb-6 text-white/70 text-sm lg:text-base leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= FINAL CTA ========================= */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${data.heroImage})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        <div className="container-max relative text-center max-w-3xl">
          <Reveal
            variant="zoom"
            as="h2"
            className="font-display text-3xl lg:text-6xl leading-[1.05] mb-6"
          >
            Il tuo prossimo passo{" "}
            <span style={{ color: "#d4ff3d" }} className="italic font-light">
              parte qui.
            </span>
          </Reveal>
          <p className="text-white/70 text-lg mb-10">
            Iscriviti al corso <strong>{data.courseName}</strong> — €{data.price}{" "}
            invece di €{data.originalPrice}. Pagamento in 3 rate senza interessi
            con PayPal.
          </p>
          <button
            type="button"
            onClick={() => goToCourse("final")}
            className="btn-primary inline-flex items-center gap-2 text-base"
            data-testid={`landing-${data.slug}-cta-final`}
          >
            <span className="flex flex-col items-center leading-none">
              <span>{data.ctaPrimary}</span>
              <span className="text-[11px] font-normal opacity-70 mt-1">Offerta lancio — €{data.price}</span>
            </span>
            <ArrowRight size={18} />
          </button>
          <p className="mt-6 text-white/40 text-xs font-mono uppercase tracking-widest">
            Posti limitati · Max 12 partecipanti per data
          </p>
          <div className="mt-6">
            <FormatInfo align="center" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
